import React, { useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Clock,
  CheckCircle,
  MessageSquare,
  Lightbulb,
  FileText,
  Calendar,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { IdentityService } from "../services/identity";

interface Feature {
  title: string;
  description: string;
  highlights: string[];
  image: string;
  imageAlt: string;
  icon: React.ReactNode;
}

const features: Feature[] = [
  {
    title: "Inline Comments & Real-Time Feedback",
    description:
      "Get instant, contextual feedback directly on your essay. Our AI provides detailed inline comments that help you understand exactly what to improve and why, making revision effortless.",
    highlights: [
      "Contextual suggestions on every paragraph",
      "Real-time feedback as you write",
      "Actionable improvement recommendations",
      "Track changes and revision history",
    ],
    image: "/images/features/inline.png",
    imageAlt: "Inline comments feature showing AI feedback on essay text",
    icon: <MessageSquare className="w-5 h-5 text-gray-600" />,
  },
  {
    title: "AI-Powered Brainstorming & Ideation",
    description:
      "Unlock your unique story with AI that helps you discover compelling experiences and craft them into powerful essays. Get personalized prompts and ideas tailored to each application.",
    highlights: [
      "Personal story mining from your experiences",
      "Unique angle discovery for each prompt",
      "Prompt-specific ideation suggestions",
      "Experience-to-essay mapping guidance",
    ],
    image: "/images/features/brainstorm.png",
    imageAlt: "AI brainstorming feature showing ideation tools",
    icon: <Lightbulb className="w-5 h-5 text-gray-600" />,
  },
  {
    title: "Comprehensive Analysis Reports",
    description:
      "Transform your essays with deep AI analysis that understands what admissions officers want. Get detailed reports on structure, authenticity, impact, and writing quality.",
    highlights: [
      "Instant comprehensive feedback reports",
      "Admissions-focused analysis metrics",
      "Content & authenticity scoring",
      "Detailed improvement roadmaps",
    ],
    image: "/images/features/report.png",
    imageAlt: "Essay analysis report showing detailed feedback metrics",
    icon: <FileText className="w-5 h-5 text-gray-600" />,
  },
  {
    title: "Calendar Events & Application Planning",
    description:
      "Never miss a deadline again. Your AI copilot tracks every application requirement, deadline, and milestone across all your target schools with smart reminders and progress tracking.",
    highlights: [
      "Multi-school deadline tracking",
      "Smart deadline reminders and alerts",
      "Application progress dashboard",
      "Requirement checklists per school",
    ],
    image: "/images/features/calendar.png",
    imageAlt: "Calendar view showing application deadlines and events",
    icon: <Calendar className="w-5 h-5 text-gray-600" />,
  },
];

const FeatureShowcase: React.FC<{ feature: Feature; index: number }> = ({
  feature,
  index,
}) => {
  const isEven = index % 2 === 0;
  const isBrainstorm = feature.image.includes("brainstorm");
  const isCalendar = feature.image.includes("calendar");

  return (
    <div className="mb-6 last:mb-0 pb-6 last:pb-0">
      {/* Single Card Container - Image and Content Together */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div
          className={`grid lg:grid-cols-2 ${
            isBrainstorm || isCalendar ? "gap-8" : "gap-0"
          }`}
        >
          {/* Content Section */}
          <div className={`p-6 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
            {/* Title with Icon */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 leading-tight">
                {feature.title}
              </h3>
            </div>

            {/* Description */}
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {feature.description}
            </p>

            {/* Divider */}
            <div className="border-t border-gray-200 mb-4"></div>

            {/* Action Items Header */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-4 bg-gray-300 rounded-full"></div>
              <div className="text-sm font-semibold text-gray-900">
                Key Features
              </div>
            </div>

            {/* Numbered List */}
            <div className="space-y-3 pl-1">
              {feature.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-start gap-3 group">
                  <div className="flex-shrink-0 w-6 h-6 rounded-md bg-gray-100 text-gray-700 text-xs font-semibold flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                    {idx + 1}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed pt-0.5 group-hover:text-gray-900 transition-colors">
                    {highlight}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Image Section - macOS Window */}
          <div
            className={`${
              isEven ? "lg:order-2" : "lg:order-1"
            } relative flex items-center justify-center min-h-[400px] rounded-lg overflow-hidden p-8`}
            style={{
              backgroundImage: `url('/images/features/macOS.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            {/* macOS Window Frame */}
            <div className="relative z-10 w-full max-w-2xl mx-auto bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200">
              {/* macOS Title Bar */}
              <div className="bg-white px-3 py-1.5 flex items-center gap-2 border-b border-gray-200">
                {/* Traffic Light Buttons */}
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                </div>
                {/* Window Title */}
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-600 font-medium">
                    {feature.title
                      .split(" &")[0]
                      .split(" ")
                      .slice(0, 2)
                      .join(" ")}
                  </span>
                </div>
              </div>
              {/* Window Content - Feature Screenshot */}
              <div
                className={`bg-white ${
                  isCalendar ? "overflow-hidden min-h-[300px]" : ""
                }`}
              >
                <img
                  src={feature.image}
                  alt={feature.imageAlt}
                  className={
                    isCalendar
                      ? "w-full h-full min-h-[300px] object-cover"
                      : "w-full h-auto object-contain scale-90"
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface FeaturesSectionProps {
  onStartReview?: () => void;
  onViewPricing?: () => void;
}

const FeaturesSection: React.FC<FeaturesSectionProps> = ({
  onStartReview,
  onViewPricing,
}) => {
  const { user, userOnboarded, loading } = useAuth();
  const [isUserReviewer, setIsUserReviewer] = useState(false);
  const [isCheckingReviewerStatus, setIsCheckingReviewerStatus] =
    useState(true);

  useEffect(() => {
    const checkReviewerStatus = async () => {
      if (user?.id) {
        setIsCheckingReviewerStatus(true);
        const identity = await IdentityService.getUserIdentity(user.id);
        const hasReviewerRole = identity.isReviewer;
        setIsUserReviewer(hasReviewerRole);
        setIsCheckingReviewerStatus(false);
      } else {
        setIsUserReviewer(false);
        setIsCheckingReviewerStatus(false);
      }
    };

    checkReviewerStatus();
  }, [user?.id, userOnboarded]);

  const handleStartClick = () => {
    if (user) {
      if (userOnboarded === false) {
        window.location.href = "/onboarding";
      } else {
        window.location.href = isUserReviewer ? "/reviewer-dashboard" : "/applicant-dashboard";
      }
    } else {
      onStartReview?.();
    }
  };

  const getButtonText = () => {
    if (!user) return "Start Your Journey";
    if (userOnboarded === false) return "Complete Onboarding";
    if (isUserReviewer) return "Go to Dashboard";
    return "Continue Your Journey";
  };

  return (
    <section
      id="features"
      className="py-24 lg:py-32 px-6 lg:px-12 bg-white border-t border-b border-gray-200 font-sans dotted-background"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 pb-8 border-b border-gray-200">
          <div className="flex items-center justify-center mb-3">
            <Badge variant="outline" className="text-sm font-medium">
              Your Complete College App Toolkit
            </Badge>
          </div>
          <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight text-gray-900 mb-3 leading-tight">
            End-to-End Application Support
          </h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            From brainstorming your first essay to tracking final deadlines, our
            AI copilot guides you through every step of the college application
            process.
          </p>
        </div>

        {/* Features Showcase */}
        <div className="mb-12">
          {features.map((feature, index) => (
            <FeatureShowcase key={index} feature={feature} index={index} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-24 pt-24 border-t border-gray-200">
          <div className="bg-gray-50 rounded-2xl p-8 lg:p-12 shadow-sm border border-gray-200">
            <div className="flex items-center justify-center mb-4">
              <Clock className="h-6 w-6 text-gray-600 mr-2" />
              <span className="text-gray-600 font-medium">
                Ready to get started?
              </span>
            </div>
            <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-3">
              Start your college application journey today
            </h3>
            <p className="text-sm text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of students who are transforming their college
              applications with AI guidance. Start with essay review and unlock
              your complete application toolkit.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-black text-white hover:bg-gray-800"
                onClick={handleStartClick}
                disabled={loading || isCheckingReviewerStatus}
              >
                {loading || isCheckingReviewerStatus
                  ? "Loading..."
                  : getButtonText()}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-gray-300 hover:bg-gray-50"
                onClick={onViewPricing}
              >
                View Pricing
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
